<template>
  <v-flex>
    <div class="v-label theme--light">{{ schema.$schema.title }}</div>

    <v-spacer />

    <v-layout column style="margin-top: 0.5em">
      <v-card elevation="0" class="border-dash">
        <v-card-title class="justify-center px-0 text-center d-flex flex-column">
          <div class="v-card-h2">Przeciągnij załącznik, aby dodać</div>

          <span>
            <v-icon large>mdi-upload</v-icon>
          </span>
        </v-card-title>
      </v-card>

      <file-upload
        ref="upload"
        v-model="files"
        class="v-btn add-file__button theme--light pointer v-size--large d-flex mb-4 manual-file-upload px-0 max-width-100"
        :accept="accept"
        :size="size"
        :multiple="multiple"
        :post-action="linkPOST"
        :drop="true"
        :drop-directory="true"
        @input-file="saveToSchema"
      />

      <v-card-title v-if="files.length" class="d-flex flex-column align-start px-0 py-2 separate--after">
        <div class="v-card-h2">Pliki:</div>

        <ul v-if="files.length" class="v-card-h4 mb-4">
          <li v-for="file in files" :key="file.id" class="mt-2">
            <span>{{ file.name }}</span>
            -
            <span>{{ file.size | formatSize }}</span>
            -
            <span v-if="file.error">błąd</span>

            <span v-else-if="file.success">wysłano</span>

            <span v-else-if="file.active">wysyłam</span>

            <span v-else>czeka na wysłanie</span>
            -
            <v-btn outlined text x-small @click.prevent="removeFile(file)">Usuń</v-btn>
          </li>
        </ul>
      </v-card-title>

      <div class="v-card-h4">
        Obsługiwane formaty załączników to: {{ formats }}. Maksymalny rozmiar pliku to {{ size | formatSize }}.
      </div>
    </v-layout>

    <div v-show="$refs.upload && $refs.upload.dropActive" class="drop-active">
      <h3>Upuść plik</h3>
    </div>

    <p class="error--text">{{ schema.$error }}</p>
  </v-flex>
</template>

<script>
import FileUpload from 'vue-upload-component';
import { mediaResource } from '@witkac/kartamiejska-resources';
import { formatSize } from '@witkac/kartamiejska-shared';

import { MediaTypeExtensionTranslation } from '@/enums/mediaType.enum';
import { config } from '@/utils/config';
import { notificationsMixin } from '@/mixins/notifications';

// @vuese
// @group Components/Forms
// Input na media
export default {
  components: { FileUpload },

  filters: { formatSize },

  mixins: [notificationsMixin],

  props: {
    // schema
    schema: { type: Object, required: true },
    // akceptowane typy plików
    accept: { type: String, default: 'image/png,image/jpeg,application/pdf' },
    // maksymalny rozmiar; 0 = brak limitu
    size: { type: Number, default: 3 * 1024 * 1024 }
  },

  data() {
    return {
      files: [],
      linkPOST: config.mediaURL
    };
  },

  computed: {
    multiple() {
      return this.schema.$schema.type === 'array';
    },
    formats() {
      return this.accept
        .split(',')
        .map(format => MediaTypeExtensionTranslation[format])
        .join(', ');
    }
  },

  mounted() {
    this.loadFile();
    this.setLabel();
  },

  methods: {
    // @vuese
    // Ustawia label dodawania ręcznie
    setLabel() {
      const label = this.$el.querySelector('.manual-file-upload label');
      if (this.multiple) {
        label.innerHTML = 'Dodaj pliki ręcznie';
      } else {
        label.innerHTML = 'Dodaj plik ręcznie';
      }
    },
    // @vuese
    // Wysyłam do schemy
    saveToSchema(newFile) {
      if (this.accept && (!this.accept.includes(newFile.type) || !newFile.type)) {
        this.removeFile(newFile);
        this.notifyError('Nieprawidłowy format pliku.');
        return;
      }

      if (this.size && newFile.size > this.size) {
        this.removeFile(newFile);
        this.notifyError(`Rozmiar pliku nie może przekroczyć ${formatSize(this.size)}.`);
        return;
      }

      if (!newFile.active && !newFile.success) {
        this.$refs.upload.active = true;
        return;
      }

      if (!newFile.success) {
        return;
      }

      const id = this.fileMediaId(newFile);
      if (this.multiple) {
        this.schema.$addRow(id);
      } else if (this.schema.$schema.type === 'number') {
        this.schema.$model = id;
      } else {
        this.schema.$model = id.toString();
      }
    },

    // @vuese
    // Usuwa plik
    removeFile(fileToRemove) {
      const index = this.files.findIndex(file => file.id === fileToRemove.id);
      this.files.splice(index, 1);
      if (fileToRemove.success) {
        this.removeFileFromSchema(fileToRemove);
      }
    },

    // @vuese
    // Usuwa plik ze schemy
    removeFileFromSchema(fileToRemove) {
      if (this.multiple) {
        const index = this.schema.$model.indexOf(this.fileMediaId(fileToRemove));
        this.schema.$removeRow(index);
      } else {
        this.schema.$setData(null);
      }
    },

    // @vuese
    // Zwraca identyfikator medii przypisanej do pliku
    fileMediaId(file) {
      return JSON.parse(file.xhr.response).id;
    },

    // @vuese
    // Ładowanie pliku
    loadFile() {
      const mediaIds = [];
      if (this.multiple) {
        mediaIds.push(...this.schema.$model);
      } else if (this.schema.$model) {
        mediaIds.push(parseInt(this.schema.$model));
      }
      if (!mediaIds.length) {
        return;
      }

      Promise.all(
        mediaIds.map(mediaId =>
          mediaResource
            .getMedia(mediaId)
            .then(mediaInfo => {
              const file = { ...mediaInfo, type: mediaInfo.contentType, success: true, name: mediaInfo.title };
              this.files.push(file);
            })
            .catch(() => this.$store.dispatch('notifyError', { title: 'Nie udało się pobrać pliku' }))
        )
      );
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.drop-active {
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  position: fixed;
  z-index: 9999;
  opacity: 0.6;
  text-align: center;
  background: #000;
}
.drop-active h3 {
  margin: -0.5em 0 0;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
  font-size: 40px;
  color: #fff;
  padding: 0;
}
.manual-file-upload label {
  position: static !important;
  color: black;
  height: 100% !important;
  line-height: 100% !important;
  font-size: inherit !important;
  background: none !important;
  opacity: unset !important;
  padding: 1rem !important;
  cursor: pointer !important;
}

.manual-file-upload:focus-within label {
  background-color: rgba($color: $color-focus, $alpha: 0.5) !important;
}

.max-width-100 {
  max-width: 100%;
}

.manual-file-upload {
  .drop-active {
    &:focus {
      border: 2px solid $color-active;
    }
  }
}
</style>
